import { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import axios from 'axios';
import { allPaymentTypes, enumPaymentTypes, DISCOUNT } from '../../consts/paymentTypes';
import { formatPrice } from '../../helpers/common';
import { getExistingPaymentTypes, getOwedForType } from './paymentHelpers';
import { CurrencyInput } from './CurrencyInput';
import { DatePickerField } from './DatePickerField';
import { FileDropzone } from './FileDropzone';

const MANUAL_BG = 'bg-[#E8F0FE]';

const TypeSelect = ({ value, onChange, paidTypes, testId }) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    return (
        <div ref={wrapperRef} className="relative">
            <button
                type="button"
                data-testid={testId}
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-1.5 w-full h-[38px] px-3 rounded-md border border-gray-300 bg-white text-sm text-left"
            >
                <span>{enumPaymentTypes[value] || value}</span>
                {paidTypes.has(value) && (
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">paid</span>
                )}
                <ChevronDownIcon className="h-4 w-4 ml-auto text-gray-400 shrink-0" />
            </button>
            {open && (
                <div className="absolute z-[70] mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg py-1 max-h-64 overflow-y-auto">
                    {allPaymentTypes.map((option) => {
                        const paid = paidTypes.has(option);
                        return (
                            <button
                                key={option}
                                type="button"
                                onClick={() => { onChange(option); setOpen(false); }}
                                className={`flex items-center gap-1.5 w-full px-3 py-1.5 text-sm text-left hover:bg-gray-100 ${paid ? 'text-gray-400' : 'text-gray-900'}`}
                            >
                                {enumPaymentTypes[option]}
                                {paid && <span className="text-[10px] uppercase tracking-wide">paid</span>}
                                {option === value && <CheckIcon className="h-3.5 w-3.5 ml-auto" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const PaymentDialog = ({
    open,
    po,
    initialPayment,
    onSave,
    onDelete,
    onClose,
    testId
}) => {
    const isEdit = !!initialPayment?.id;
    const [form, setForm] = useState(initialPayment || {});
    const [warnings, setWarnings] = useState(null);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        setForm(initialPayment || {});
        setWarnings(null);
        setConfirmingDelete(false);
        setAttachments([]);
    }, [initialPayment, open]);

    if (!open || !initialPayment) return null;

    const existingTypes = getExistingPaymentTypes(
        (po.payments || []).filter((payment) => payment.id !== initialPayment.id)
    );
    const owedForType = getOwedForType(po, form.type, isEdit ? initialPayment.id : undefined);

    const handleChange = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

    const uploadAttachments = async () => {
        if (attachments.length === 0) return true;
        setUploading(true);
        try {
            await Promise.all(attachments.map((file) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('referenceType', 'PurchaseOrder');
                formData.append('referenceId', po.id);
                return axios.post('/api/v1/files/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }));
            return true;
        } catch (error) {
            console.error('Error uploading attachment:', error);
            toast.error('Failed to upload attachments', { position: 'bottom-right' });
            return false;
        } finally {
            setUploading(false);
        }
    };

    const savePayment = async (paymentForm) => {
        const uploaded = await uploadAttachments();
        if (!uploaded) return;
        onSave(paymentForm);
    };

    const handleSaveClick = () => {
        const amount = Number(form.amount);
        if (form.amount === '' || form.amount === undefined || Number.isNaN(amount)) {
            toast.error('Enter a valid payment amount', { position: 'bottom-right' });
            return;
        }
        if (form.type === DISCOUNT && amount >= 0) {
            toast.error('Discount amount must be less than 0', { position: 'bottom-right' });
            return;
        }
        const messages = [];
        if (!isEdit && existingTypes.has(form.type)) {
            messages.push(`${po.poNumber} already has a ${enumPaymentTypes[form.type]} payment recorded.`);
        }
        if (form.type !== DISCOUNT && amount > owedForType + 0.005) {
            messages.push(
                `This payment of $${formatPrice(amount)} exceeds the $${formatPrice(owedForType)} owed for ${enumPaymentTypes[form.type]} — $${formatPrice(amount - owedForType)} over.`
            );
        }
        if (messages.length > 0) {
            setWarnings(messages);
            return;
        }
        savePayment(form);
    };

    const saveLabel = isEdit
        ? 'Save Changes'
        : `Add Payment${attachments.length > 0 ? ` + ${attachments.length} File${attachments.length > 1 ? 's' : ''}` : ''}`;

    return (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center overflow-y-auto p-4" data-testid={testId}>
            <div className="fixed inset-0 bg-gray-700 opacity-40" onClick={onClose} />
            <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl z-[61] max-h-[90vh] overflow-y-auto">
                {warnings ? (
                    <div className="p-5">
                        <h3 className="text-lg font-bold mb-3">Please confirm this payment</h3>
                        <div className="space-y-2 text-sm text-gray-700">
                            {warnings.map((message, index) => (
                                <p key={index}>{message}</p>
                            ))}
                            <p>Do you wish to proceed?</p>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                onClick={() => setWarnings(null)}
                                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                            >
                                No, go back
                            </button>
                            <button
                                onClick={() => savePayment(form)}
                                data-testid={testId ? `${testId}-confirm-save` : undefined}
                                className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-gray-800"
                            >
                                Yes, proceed
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold">
                                {isEdit ? 'Edit Payment' : 'Add Payment'} — {po.poNumber}
                            </h3>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Amount</label>
                                    <CurrencyInput
                                        value={form.amount ?? ''}
                                        onValueChange={(v) => handleChange('amount', v)}
                                        className={`w-full ${MANUAL_BG}`}
                                        testId={testId ? `${testId}-amount` : undefined}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Date</label>
                                    <DatePickerField
                                        value={form.date || ''}
                                        onChange={(v) => handleChange('date', v)}
                                        allowClear={false}
                                        className="w-full"
                                        testId={testId ? `${testId}-date` : undefined}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Type</label>
                                <TypeSelect
                                    value={form.type || ''}
                                    onChange={(v) => handleChange('type', v)}
                                    paidTypes={existingTypes}
                                    testId={testId ? `${testId}-type` : undefined}
                                />
                                <p className="text-xs text-gray-500 pt-0.5">
                                    Owed for {enumPaymentTypes[form.type] || form.type}:{' '}
                                    <span className="font-semibold text-gray-900 tabular-nums">${formatPrice(owedForType)}</span>
                                </p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Transaction ID</label>
                                <input
                                    type="text"
                                    value={form.transactionId || ''}
                                    data-testid={testId ? `${testId}-transactionId` : undefined}
                                    onChange={(e) => handleChange('transactionId', e.target.value)}
                                    placeholder="e.g. P26071402 - 1234567"
                                    className={`w-full text-sm border-gray-300 rounded-md ${MANUAL_BG}`}
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Note</label>
                                <textarea
                                    value={form.note || ''}
                                    rows={isEdit ? 2 : 1}
                                    data-testid={testId ? `${testId}-note` : undefined}
                                    onChange={(e) => handleChange('note', e.target.value)}
                                    placeholder="e.g. Bill"
                                    className={`w-full text-sm border-gray-300 rounded-md resize-y ${MANUAL_BG}`}
                                />
                            </div>
                            {!isEdit && (
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">
                                        Attachments <span className="text-gray-500 font-normal">(optional — wire confirmation, invoice)</span>
                                    </label>
                                    <FileDropzone files={attachments} onChange={setAttachments} compact />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center justify-between mt-5 gap-2">
                            <div>
                                {isEdit && onDelete && (
                                    <button
                                        onClick={() => {
                                            if (confirmingDelete) {
                                                onDelete(initialPayment.id);
                                            } else {
                                                setConfirmingDelete(true);
                                            }
                                        }}
                                        data-testid={testId ? `${testId}-delete` : undefined}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${confirmingDelete ? 'bg-red-600 text-white hover:bg-red-700' : 'text-red-600 hover:bg-red-50'}`}
                                    >
                                        {confirmingDelete ? 'You sure?' : 'Delete'}
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveClick}
                                    disabled={uploading}
                                    data-testid={testId ? `${testId}-save` : undefined}
                                    className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-gray-800 disabled:opacity-50"
                                >
                                    {uploading ? 'Uploading…' : saveLabel}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
